import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { ScrollText, RefreshCw, Pause, Play, Trash2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';

const SystemLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);
  const logsEndRef = useRef(null);
  const logsContainerRef = useRef(null);

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchLogs(true); // silent refresh
      }, 5000); // cada 5 segundos
    }
    return () => clearInterval(interval);
  }, [autoRefresh]);

  useEffect(() => {
    // Auto-scroll al final cuando llegan nuevos logs
    if (autoRefresh && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoRefresh]);

  const fetchLogs = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setLogs(response.data.logs);
      }
    } catch (err) {
      console.error('Error al cargar logs:', err);
      setError(err.response?.data?.detail || 'Error al cargar los logs del sistema');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const clearScreen = () => {
    setLogs([]);
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  const handleResetSystem = async () => {
    try {
      setResetting(true);
      const response = await axios.post(`${API}/system/reset`);
      
      if (response.data.success) {
        toast.success('Sistema reseteado exitosamente');
        setShowResetDialog(false);
        
        // Mostrar resumen de lo eliminado
        const deleted = response.data.deleted;
        const summary = `
          Eliminado:
          - ${deleted.productos} productos
          - ${deleted.facturas} facturas
          - ${deleted.clientes} clientes
          - ${deleted.caja} registros de caja
          - ${deleted.usuarios} usuarios
        `;
        toast.success(summary, { duration: 8000 });
        
        // Refrescar logs
        fetchLogs();
      }
    } catch (error) {
      console.error('Error al resetear sistema:', error);
      toast.error('Error al resetear el sistema: ' + (error.response?.data?.detail || error.message));
    } finally {
      setResetting(false);
    }
  };

  const getColorClass = (color) => {
    const colorMap = {
      red: 'text-red-600 bg-red-50 border-red-200',
      blue: 'text-blue-600 bg-blue-50 border-blue-200',
      green: 'text-green-600 bg-green-50 border-green-200',
      purple: 'text-purple-600 bg-purple-50 border-purple-200',
      orange: 'text-orange-600 bg-orange-50 border-orange-200',
      gray: 'text-gray-600 bg-gray-50 border-gray-200',
    };
    return colorMap[color] || 'text-gray-800 bg-white border-gray-200';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <ScrollText className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Logs del Sistema</h1>
            <p className="text-sm text-gray-600">Visualización en tiempo real de errores del sistema</p>
          </div>
        </div>

        <div className="flex gap-2">
          {/* Reset System Dialog */}
          <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 border-red-500 text-red-600 hover:bg-red-50"
              >
                <AlertTriangle className="h-4 w-4" />
                Reset Sistema
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                  ⚠️ Confirmar Reset del Sistema
                </DialogTitle>
                <DialogDescription className="space-y-3">
                  <p className="font-semibold text-amber-600">
                    Esta acción es IRREVERSIBLE y borrará TODOS los datos excepto:
                  </p>
                  
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <p className="font-semibold text-green-800 mb-2">✓ Se Mantendrá:</p>
                    <ul className="text-sm text-green-700 space-y-1 ml-4">
                      <li>• Usuario Superadmin</li>
                      <li>• Usuario Admin</li>
                      <li>• Cliente CONSUMIDOR FINAL</li>
                      <li>• Configuración del negocio</li>
                      <li>• Parámetros del sistema</li>
                      <li>• Configuración de etiquetas</li>
                    </ul>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded p-3">
                    <p className="font-semibold text-red-800 mb-2">✗ Se Eliminará:</p>
                    <ul className="text-sm text-red-700 space-y-1 ml-4">
                      <li>• TODOS los productos</li>
                      <li>• TODAS las facturas</li>
                      <li>• TODOS los registros de caja</li>
                      <li>• TODAS las ofertas</li>
                      <li>• TODOS los movimientos de inventario</li>
                      <li>• Otros usuarios y clientes</li>
                    </ul>
                  </div>

                  <p className="text-red-600 font-bold">
                    ¿Estás seguro de que deseas continuar?
                  </p>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowResetDialog(false)}
                  disabled={resetting}
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleResetSystem}
                  disabled={resetting}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {resetting ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Reseteando...
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Sí, Resetear Sistema
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Button
            onClick={clearScreen}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Limpiar Pantalla
          </Button>
          
          <Button
            onClick={toggleAutoRefresh}
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            className="flex items-center gap-2"
          >
            {autoRefresh ? (
              <>
                <Pause className="h-4 w-4" />
                Pausar
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                Reanudar
              </>
            )}
          </Button>

          <Button
            onClick={() => fetchLogs()}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-sm">
              <span className="text-gray-600">Total de líneas:</span>
              <span className="ml-2 font-semibold text-gray-900">{logs.length}</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-600">Auto-refresh:</span>
              <span className={`ml-2 font-semibold ${autoRefresh ? 'text-green-600' : 'text-gray-500'}`}>
                {autoRefresh ? 'Activo (cada 5s)' : 'Pausado'}
              </span>
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex gap-3 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-100 border border-red-200 rounded"></div>
              <span className="text-gray-600">Backend Error</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-100 border border-red-200 rounded"></div>
              <span className="text-gray-600">Frontend Error</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-orange-100 border border-orange-200 rounded"></div>
              <span className="text-gray-600">Supervisor</span>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Logs Container */}
      <div className="bg-gray-900 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div
          ref={logsContainerRef}
          className="p-4 overflow-y-auto font-mono text-sm"
          style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '400px' }}
        >
          {logs.length === 0 ? (
            <div className="text-green-400 text-center py-8">
              ✅ No hay errores en el sistema. Todo funciona correctamente.
            </div>
          ) : (
            logs.map((log, index) => (
              <div
                key={index}
                className={`mb-1 p-2 rounded border ${getColorClass(log.color)}`}
              >
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-xs whitespace-nowrap">
                    [{log.source}]
                  </span>
                  <span className="flex-1 break-all">{log.content}</span>
                </div>
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
      </div>

      {/* Footer Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Nota:</strong> Los logs se actualizan automáticamente cada 5 segundos. 
          Puedes pausar la actualización automática haciendo clic en el botón "Pausar".
          Se muestran solo los errores de las últimas 200 líneas de cada fuente (Backend Error, Frontend Error, Supervisor).
        </p>
      </div>
    </div>
  );
};

export default SystemLogs;
